// Digitally sign the document with a certificate.
//
// Not to be confused with the Sign tool, which draws a picture of a
// signature — a useful thing that proves nothing. This one appends a
// cryptographic signature over the exact bytes of the exported file, so a
// reader can tell whether anybody has changed it since.
//
// The private key never leaves this page: the certificate is read here,
// the digest comes from the worker, and the signature is assembled in the
// browser. Nothing is uploaded, which is the whole reason to do it here
// rather than on a server.
import { useEffect, useMemo, useRef, useState } from "react";
import type { PageInfo } from "../lib/protocol.ts";

export type SignPlacement = "invisible" | "bottom-left" | "bottom-right" | "top-left" | "top-right";

export interface CertifyRequest {
  source: { kind: "p12"; file: File; password: string } | { kind: "pem"; cert: string; key: string };
  name: string;
  reason: string;
  location: string;
  contactInfo: string;
  placement: SignPlacement;
  pageIndex: number;
}

/** The stamp's box for a corner, in fitz page space. */
export function placementRect(
  placement: Exclude<SignPlacement, "invisible">,
  page: PageInfo,
  w = 210,
  h = 58
): [number, number, number, number] {
  const m = 36;
  const x = placement.endsWith("left") ? m : Math.max(m, page.width - m - w);
  const y = placement.startsWith("top") ? m : Math.max(m, page.height - m - h);
  return [x, y, x + w, y + h];
}

const PLACEMENTS: { id: SignPlacement; label: string }[] = [
  { id: "invisible", label: "No visible stamp" },
  { id: "bottom-left", label: "Bottom left" },
  { id: "bottom-right", label: "Bottom right" },
  { id: "top-left", label: "Top left" },
  { id: "top-right", label: "Top right" },
];

export function CertifyDialog({
  pages,
  currentPage,
  defaultName,
  busy,
  status,
  onCancel,
  onSign,
}: {
  pages: PageInfo[];
  currentPage: number;
  /** The comment-author setting, as a starting point for /Name. */
  defaultName: string;
  busy: boolean;
  /** Progress or failure from the last attempt. */
  status: { kind: "info" | "error" | "ok"; text: string } | null;
  onCancel: () => void;
  onSign: (req: CertifyRequest) => void;
}) {
  const [tab, setTab] = useState<"p12" | "pem">("p12");
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [certPem, setCertPem] = useState("");
  const [keyPem, setKeyPem] = useState("");
  const [name, setName] = useState(defaultName);
  const [reason, setReason] = useState("");
  const [location, setLocation] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [placement, setPlacement] = useState<SignPlacement>("invisible");
  const [pageIndex, setPageIndex] = useState(currentPage);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  const ready = useMemo(
    () => (tab === "p12" ? !!file : certPem.includes("BEGIN") && keyPem.includes("BEGIN")),
    [tab, file, certPem, keyPem]
  );

  const submit = () => {
    if (!ready || busy) return;
    onSign({
      source:
        tab === "p12"
          ? { kind: "p12", file: file!, password }
          : { kind: "pem", cert: certPem, key: keyPem },
      name,
      reason,
      location,
      contactInfo,
      placement,
      pageIndex,
    });
  };

  const field = "h-8 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-xs";
  const label = "mb-1 block text-[11px] font-medium text-[color:var(--color-fg-muted)]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Digitally sign"
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] shadow-xl"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] p-3">
          <i className="ri-shield-check-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
          <h2 className="text-sm font-semibold">Digitally sign</h2>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
          <p className="rounded-md bg-[color:var(--color-bg-subtle)] p-2 text-[11px] leading-relaxed text-[color:var(--color-fg-muted)]">
            Signs the exported file with your certificate so a reader can tell whether it has been
            changed since. Your private key stays in this page — nothing is uploaded. Editing a
            signed file afterwards breaks the signature, so this is the last step.
          </p>

          <div role="tablist" aria-label="Certificate format" className="flex gap-1">
            {(["p12", "pem"] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={`h-7 rounded-md px-2.5 text-xs ${
                  tab === t
                    ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                    : "border border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-subtle)]"
                }`}
              >
                {t === "p12" ? "Certificate file (.p12 / .pfx)" : "PEM"}
              </button>
            ))}
          </div>

          {tab === "p12" ? (
            <div className="space-y-2">
              <div>
                <span className={label}>Certificate</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] px-2.5 text-xs hover:bg-[color:var(--color-bg-subtle)]"
                  >
                    <i className="ri-folder-open-line" aria-hidden /> Choose…
                  </button>
                  <span className="min-w-0 flex-1 truncate text-[11px] text-[color:var(--color-fg-muted)]">
                    {file ? file.name : "No file chosen"}
                  </span>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".p12,.pfx,application/x-pkcs12"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <label className="block">
                <span className={label}>Password</span>
                <input
                  type="password"
                  value={password}
                  autoComplete="off"
                  onChange={(e) => setPassword(e.target.value)}
                  className={field}
                />
              </label>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block">
                <span className={label}>Certificate (PEM, chain allowed)</span>
                <textarea
                  value={certPem}
                  rows={3}
                  spellCheck={false}
                  placeholder="-----BEGIN CERTIFICATE-----"
                  onChange={(e) => setCertPem(e.target.value)}
                  className="w-full resize-y rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-2 font-mono text-[10px]"
                />
              </label>
              <label className="block">
                <span className={label}>Private key (unencrypted PKCS#8)</span>
                <textarea
                  value={keyPem}
                  rows={3}
                  spellCheck={false}
                  placeholder="-----BEGIN PRIVATE KEY-----"
                  onChange={(e) => setKeyPem(e.target.value)}
                  className="w-full resize-y rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-2 font-mono text-[10px]"
                />
              </label>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 border-t border-[color:var(--color-border)] pt-3">
            <label className="block">
              <span className={label}>Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
            </label>
            <label className="block">
              <span className={label}>Reason</span>
              <input
                value={reason}
                placeholder="e.g. Approved"
                onChange={(e) => setReason(e.target.value)}
                className={field}
              />
            </label>
            <label className="block">
              <span className={label}>Location</span>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className={field}
              />
            </label>
            <label className="block">
              <span className={label}>Contact</span>
              <input
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                className={field}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className={label}>Visible stamp</span>
              <select
                value={placement}
                onChange={(e) => setPlacement(e.target.value as SignPlacement)}
                className={field}
              >
                {PLACEMENTS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {placement !== "invisible" && (
              <label className="block">
                <span className={label}>On page</span>
                <select
                  value={pageIndex}
                  onChange={(e) => setPageIndex(Number(e.target.value))}
                  className={field}
                >
                  {pages.map((_, i) => (
                    <option key={i} value={i}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {status && (
            <p
              className={`rounded-md p-2 text-[11px] leading-relaxed ${
                status.kind === "error"
                  ? "bg-[#e0313115] text-[#e03131]"
                  : status.kind === "ok"
                    ? "bg-[#2f9e4415] text-[#2f9e44]"
                    : "bg-[color:var(--color-bg-subtle)] text-[color:var(--color-fg-muted)]"
              }`}
            >
              {status.text}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-[color:var(--color-border)] p-3">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-xs hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready || busy}
            onClick={submit}
            className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-xs text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
          >
            {busy ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden /> Signing…
              </>
            ) : (
              <>
                <i className="ri-shield-check-line" aria-hidden /> Sign &amp; download
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
